import { Link, useSearchParams } from 'react-router-dom'
import type { ConstructorDriver, ConstructorStats, DriverStats } from '../api/types'
import { comparePoints, formatPoints } from '../lib/decimal'
import { useUniversePath } from '../lib/universe'
import { by, useSort, type Column } from '../lib/useSort'

type Sorting<K extends string> = { key: K; desc: boolean; toggle: (key: K) => void }

function SortHeader<K extends string>({
  label,
  name,
  sort,
  num = true,
  title,
}: {
  label: string
  name: K
  sort: Sorting<K>
  num?: boolean
  title?: string
}) {
  const active = sort.key === name
  return (
    <th
      scope="col"
      className={num ? 'num' : undefined}
      aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : undefined}
      title={title}
    >
      <button type="button" className="sort" onClick={() => sort.toggle(name)}>
        {label}
        <span aria-hidden="true">{active ? (sort.desc ? ' ▼' : ' ▲') : ''}</span>
      </button>
    </th>
  )
}

/** Narrows a table by name, in the URL beside its order. */
export function NameFilter({ label }: { label: string }) {
  const [params, setParams] = useSearchParams()
  return (
    <label className="search">
      <span className="muted">{label}</span>
      <input
        type="search"
        value={params.get('q') ?? ''}
        placeholder="Name"
        onChange={(event) => {
          const updated = new URLSearchParams(params)
          if (event.target.value) updated.set('q', event.target.value)
          else updated.delete('q')
          // Replace rather than push: typing should not fill the history.
          setParams(updated, { replace: true })
        }}
      />
    </label>
  )
}

/** Accent- and case-insensitive, so "raikkonen" finds Räikkönen. */
export function matches(name: string, query: string): boolean {
  const fold = (text: string) =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
  return fold(name).includes(fold(query.trim()))
}

const COUNTS = ['wins', 'podiums', 'fastest_laps', 'poles', 'starts'] as const

function counts<T extends Record<(typeof COUNTS)[number], number>>() {
  return Object.fromEntries(
    COUNTS.map((name) => [name, { compare: by<T>((row) => row[name]), descFirst: true }]),
  ) as Record<(typeof COUNTS)[number], Column<T>>
}

const DRIVER_COLUMNS = {
  name: { compare: by<DriverStats>((row) => `${row.last_name} ${row.first_name}`) },
  teams: { compare: by<DriverStats>((row) => row.teams), descFirst: true },
  team: { compare: by<DriverStats>((row) => row.last_team?.name) },
  years: { compare: by<DriverStats>((row) => row.first_year) },
  seasons: { compare: by<DriverStats>((row) => row.seasons), descFirst: true },
  country: { compare: by<DriverStats>((row) => row.driver.nationality?.name) },
  titles: { compare: by<DriverStats>((row) => row.titles), descFirst: true },
  ...counts<DriverStats>(),
  points: {
    compare: (a: DriverStats, b: DriverStats) => comparePoints(a.points, b.points),
    descFirst: true,
  },
}

export function DriverStatsTable({ rows }: { rows: DriverStats[] }) {
  const [params] = useSearchParams()
  const query = params.get('q') ?? ''
  const shown = query
    ? rows.filter((row) => matches(`${row.first_name} ${row.last_name}`, query))
    : rows
  const { sorted, ...sort } = useSort(shown, DRIVER_COLUMNS, 'wins')
  const to = useUniversePath()

  return (
    <div className="table-scroll">
      <table className="grid careers">
        <thead>
          <tr>
            <SortHeader label="Driver" name="name" sort={sort} num={false} />
            <SortHeader label="Teams" name="teams" sort={sort} />
            <SortHeader
              label="Last team"
              name="team"
              sort={sort}
              num={false}
              title="The current team, for a driver still racing"
            />
            <SortHeader label="Years" name="years" sort={sort} num={false} />
            <SortHeader
              label="Seasons"
              name="seasons"
              sort={sort}
              title="Seasons with at least one start"
            />
            <SortHeader label="Country" name="country" sort={sort} num={false} />
            <SortHeader label="Titles" name="titles" sort={sort} />
            <SortHeader label="Wins" name="wins" sort={sort} />
            <SortHeader label="Podiums" name="podiums" sort={sort} />
            <SortHeader label="Fastest laps" name="fastest_laps" sort={sort} />
            <SortHeader label="Poles" name="poles" sort={sort} />
            <SortHeader label="Starts" name="starts" sort={sort} />
            <SortHeader label="Points" name="points" sort={sort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.driver.slug}>
              <td>
                <Link to={to(`/drivers/${row.driver.slug}`)}>
                  <strong>{row.last_name}</strong> {row.first_name}
                </Link>
              </td>
              <td className="num">{row.teams}</td>
              <td>
                {row.last_team ? (
                  <Link to={to(`/constructors/${row.last_team.slug}`)}>
                    {row.last_team.name}
                  </Link>
                ) : null}
                {row.current ? <span className="badge">current</span> : null}
              </td>
              <td>
                <Years first={row.first_year} last={row.last_year} />
              </td>
              <td className="num">{row.seasons}</td>
              <td>{row.driver.nationality?.name ?? ''}</td>
              <td className="num">{row.titles || ''}</td>
              <td className="num">{row.wins}</td>
              <td className="num">{row.podiums}</td>
              <td className="num">{row.fastest_laps}</td>
              <td className="num">{row.poles}</td>
              <td className="num">{row.starts}</td>
              <td className="num">{formatPoints(row.points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Showing shown={sorted.length} total={rows.length} />
    </div>
  )
}

const CONSTRUCTOR_COLUMNS = {
  name: { compare: by<ConstructorStats>((row) => row.constructor.name) },
  drivers: { compare: by<ConstructorStats>((row) => row.drivers), descFirst: true },
  years: { compare: by<ConstructorStats>((row) => row.first_year) },
  titles: { compare: by<ConstructorStats>((row) => row.titles), descFirst: true },
  ...counts<ConstructorStats>(),
  points: {
    compare: (a: ConstructorStats, b: ConstructorStats) => comparePoints(a.points, b.points),
    descFirst: true,
  },
}

export function ConstructorStatsTable({ rows }: { rows: ConstructorStats[] }) {
  const [params] = useSearchParams()
  const query = params.get('q') ?? ''
  const shown = query ? rows.filter((row) => matches(row.constructor.name, query)) : rows
  const { sorted, ...sort } = useSort(shown, CONSTRUCTOR_COLUMNS, 'wins')
  const to = useUniversePath()

  return (
    <div className="table-scroll">
      <table className="grid careers">
        <thead>
          <tr>
            <SortHeader label="Constructor" name="name" sort={sort} num={false} />
            <SortHeader label="Drivers" name="drivers" sort={sort} />
            <th scope="col">Current drivers</th>
            <SortHeader label="Years" name="years" sort={sort} num={false} />
            <SortHeader label="Titles" name="titles" sort={sort} />
            <SortHeader label="Wins" name="wins" sort={sort} />
            <SortHeader label="Podiums" name="podiums" sort={sort} />
            <SortHeader label="Fastest laps" name="fastest_laps" sort={sort} />
            <SortHeader label="Poles" name="poles" sort={sort} />
            <SortHeader label="Starts" name="starts" sort={sort} />
            <SortHeader label="Points" name="points" sort={sort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.constructor.slug}>
              <td>
                <Link to={to(`/constructors/${row.constructor.slug}`)}>
                  <strong>{row.constructor.name}</strong>
                </Link>
              </td>
              <td className="num">{row.drivers}</td>
              <td>
                {row.current_drivers.map((driver, index) => (
                  <span key={driver.slug}>
                    {index > 0 ? ', ' : ''}
                    <Link to={to(`/drivers/${driver.slug}`)}>{driver.name}</Link>
                  </span>
                ))}
              </td>
              <td>
                <Years first={row.first_year} last={row.last_year} />
              </td>
              <td className="num">{row.titles || ''}</td>
              <td className="num">{row.wins}</td>
              <td className="num">{row.podiums}</td>
              <td className="num">{row.fastest_laps}</td>
              <td className="num">{row.poles}</td>
              <td className="num">{row.starts}</td>
              <td className="num">{formatPoints(row.points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Showing shown={sorted.length} total={rows.length} />
    </div>
  )
}

const TEAM_DRIVER_COLUMNS = {
  name: { compare: by<ConstructorDriver>((row) => row.driver.name) },
  years: { compare: by<ConstructorDriver>((row) => row.years[0]) },
  titles: { compare: by<ConstructorDriver>((row) => row.titles), descFirst: true },
  wins: { compare: by<ConstructorDriver>((row) => row.wins), descFirst: true },
  poles: { compare: by<ConstructorDriver>((row) => row.poles), descFirst: true },
  podiums: { compare: by<ConstructorDriver>((row) => row.podiums), descFirst: true },
  points: {
    compare: (a: ConstructorDriver, b: ConstructorDriver) => comparePoints(a.points, b.points),
    descFirst: true,
  },
  starts: { compare: by<ConstructorDriver>((row) => row.starts), descFirst: true },
}

/** Everyone who drove for one constructor, with what they did there. */
export function ConstructorDriversTable({ rows }: { rows: ConstructorDriver[] }) {
  const { sorted, ...sort } = useSort(rows, TEAM_DRIVER_COLUMNS, 'years')
  const to = useUniversePath()

  return (
    <div className="table-scroll">
      <table className="grid careers">
        <thead>
          <tr>
            <SortHeader label="Driver" name="name" sort={sort} num={false} />
            <SortHeader
              label="Titles"
              name="titles"
              sort={sort}
              title="Drivers’ championships won in a season driven for this team"
            />
            <SortHeader label="Wins" name="wins" sort={sort} />
            <SortHeader label="Poles" name="poles" sort={sort} />
            <SortHeader label="Podiums" name="podiums" sort={sort} />
            <SortHeader
              label="Points"
              name="points"
              sort={sort}
              title="Scored with this team, sprints included"
            />
            <SortHeader label="Races" name="starts" sort={sort} title="Grands Prix started" />
            <SortHeader label="Years" name="years" sort={sort} num={false} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.driver.slug}>
              <td>
                <Link to={to(`/drivers/${row.driver.slug}`)}>{row.driver.name}</Link>
              </td>
              <td className="num">{row.titles || ''}</td>
              <td className="num">{row.wins}</td>
              <td className="num">{row.poles}</td>
              <td className="num">{row.podiums}</td>
              <td className="num">{formatPoints(row.points, '0')}</td>
              <td className="num">{row.starts}</td>
              <td>{spans(row.years)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Showing shown={sorted.length} total={rows.length} />
    </div>
  )
}

/** Sorted years as runs: [1987, 1988, 1989, 1993] → "1987–1989, 1993". */
export function spans(years: number[]): string {
  const runs: number[][] = []
  for (const year of years) {
    const run = runs.at(-1)
    if (run && run[1] === year - 1) run[1] = year
    else runs.push([year, year])
  }
  return runs
    .map(([first, last]) => (first === last ? `${first}` : `${first}–${last}`))
    .join(', ')
}

function Years({
  first,
  last,
}: {
  first: number | null | undefined
  last: number | null | undefined
}) {
  if (!first) return null
  return <>{first === last ? first : `${first}–${last}`}</>
}

function Showing({ shown, total }: { shown: number; total: number }) {
  return (
    <p className="muted">
      {shown === total ? `${total} in all.` : `Showing ${shown} of ${total}.`}
    </p>
  )
}
